import * as THREE from 'three'

import { AIRCRAFT, CARRIER, ENV, PATTERN, TARGET_TOUCHDOWN_FT } from '../config/constants.js'
import { LEGS, LEG_BY_ID } from './legs.js'
import { burbleFpm, gust, turbulenceScale } from './air.js'
import {
  RECOVERY_WEIGHT_LBS,
  SWEEP_RATE_DEG_S,
  aoaUnits,
  autoSweepDeg,
  onSpeedKtas,
  solveAlphaDeg,
} from './aero.js'
import {
  DEG,
  FT_TO_M,
  KT_TO_FPS,
  M_TO_FT,
  RAD,
  clamp,
  damp,
  deltaDeg,
  wrapDeg360,
} from '../util/units.js'

const G_FPS2 = 32.174

/** How far ahead the track controller aims when it is off the line. */
const CAPTURE_LEAD_FT = 4200
/** Bank commanded per degree of heading error. */
const BANK_PER_DEG = 1.35
/** Stick rate. A Tomcat rolls a great deal faster than this; the pattern does not. */
const ROLL_RATE_DEG_S = 32

/**
 * Roll ACCELERATION, degrees per second squared.
 *
 * This is the number that stops the aeroplane looking like a model on a wire.
 * Clamping the bank angle's rate of change — which is what this did first —
 * produces a roll that starts at full rate in one frame, holds it exactly, and
 * stops dead: the measured roll acceleration was 3,900 deg/s2, and a step that
 * violent has no equivalent in anything with mass. A real jet's roll builds and
 * dies over about half a second because the pilot's hand does, and half a
 * second of onset is the entire difference between "flown" and "animated".
 */
const ROLL_ACCEL_DEG_S2 = 55

/** How hard the pilot chases a bank angle: roll rate commanded per degree out. */
const ROLL_RATE_PER_DEG = 2.4

/**
 * Rate limit on the commanded vertical speed, fpm per second.
 *
 * The guidance can change its mind instantly — a leg boundary swaps one
 * altitude law for another between frames — and without this the aeroplane
 * inherits the discontinuity as a 38,000 fpm/s pitch snap.
 */
const VS_CMD_RATE_FPM_S = 420

/**
 * The aeroplane, flown for you.
 *
 * This replaces the flight model entirely. It is not a simplification of one —
 * there are no forces here, and no attempt to pretend there are. What it is is
 * a *kinematically honest* aeroplane: it turns at the radius its bank angle and
 * speed imply, it holds the angle of attack its weight and load factor imply,
 * and it cannot do anything a Tomcat could not do. Fly a 27 degree bank at 145
 * knots and you get a 3,650 ft radius, because that is what the arithmetic
 * says, not because someone drew a spline through some waypoints.
 *
 * That distinction matters for a procedures trainer. The pattern has to *look*
 * right from the cockpit and the numbers on the HUD have to agree with what
 * you see out of the window, or you learn the wrong sight picture. A spline
 * would give a pretty path with a nonsense attitude on it.
 *
 * Two controllers do all the work:
 *
 *   TRACK  captures a straight ground track by aiming at a point ahead on it,
 *          the way a flight director does. Self-correcting, so however untidily
 *          the previous turn ended, the next straight leg tidies it up.
 *   TURN   holds a bank angle. The radius is the point of the manoeuvre.
 *
 * Everything is computed relative to the ship, which is moving. The groove in
 * particular is flown against a centreline that is sliding sideways under the
 * aeroplane at 25 knots, and that is why the jet sits crabbed a few degrees
 * right of the deck heading all the way down.
 */
export class RailsFlight {
  /**
   * @param {import('../world/Carrier.js').Carrier} carrier
   * @param {() => boolean} canLeaveLeg  asked before every leg change; when it
   *        says no the pattern holds where it is rather than pressing on
   *        without you
   */
  constructor(carrier, canLeaveLeg = () => true, options = {}) {
    this.carrier = carrier
    this.canLeaveLeg = canLeaveLeg

    /**
     * Where the flap position comes from.
     *
     * This is not a detail. The angle of attack is solved from lift, and full
     * flap is worth 0.62 of lift coefficient — about 25 knots of approach
     * speed. If the solve reads the flap position off the LEG SCHEDULE rather
     * than off the pilot's handle, then the moment you roll out downwind the
     * indexer shows a lit doughnut at exactly 15 units with the flaps visibly
     * up on the model, and the trainer has flatly contradicted its own
     * teaching point about why the flaps matter.
     *
     * Defaults to the leg schedule so the pattern can be flown and measured
     * with no pilot attached, which is what the tests do.
     */
    this.flapSource = options.flapSource ?? null

    this.state = makeState()
    this.nav = makeNav()

    this._euler = new THREE.Euler(0, 0, 0, 'YXZ')
    this._v = new THREE.Vector3()
    this._scratch = new THREE.Vector3()

    this.reset()
  }

  /* =================================================================== *
   *  RESET                                                              *
   * =================================================================== */

  /**
   * Put the aircraft at the start of a leg, in the configuration and attitude
   * that leg expects. Starting anywhere other than INITIAL is a practice aid:
   * you should be able to drill the groove without flying three miles of
   * downwind first.
   */
  reset(legId = 'INITIAL') {
    const index = Math.max(0, LEGS.findIndex((l) => l.id === legId))
    this.legIndex = index
    const leg = LEGS[index]

    this.time = 0
    this.legTime = 0
    this.turnedDeg = 0
    this.holding = false
    this.holdReason = ''
    this.stoppedFor = 0
    this.touchdown = null
    this.wire = 0
    this.grooveEnteredAt = null
    this.grooveTimeS = 0
    this.weightLbs = RECOVERY_WEIGHT_LBS
    this._closureFps = 0
    this._glidepathLatched = false
    this._turnStartHeadingErrDeg = 189
    this._lastCommandedAlt = null
    this.vsFpm = 0
    this._wantedFpm = 0
    this._vsControl = 0
    this._turbScale = 0

    this.bankDeg = 0
    this.rollRateDegS = 0
    this.flapNorm = leg.flapNorm ?? 0
    // Wings aft at the initial, as the chart shows, programming forward as you
    // run up the starboard side. Anywhere else in the pattern they are already
    // where the schedule wants them.
    this.sweepDeg = index === 0 ? AIRCRAFT.SWEEP_MAX_DEG : AIRCRAFT.SWEEP_MIN_DEG

    const brc = this.carrier.headingDeg
    const placement = this._startPlacement(leg, brc)

    this.headingDeg = placement.headingDeg
    this.altFt = placement.altFt
    this.ktas = placement.ktas
    this.position = placement.world

    this._syncState(0)
    this._updateNav(0)
  }

  /** Where each leg begins, in the ship's frame. */
  _startPlacement(leg, brc) {
    const c = this.carrier
    const world = new THREE.Vector3()

    switch (leg.id) {
      case 'BREAK':
        c.shipToWorld(CARRIER.HULL_LENGTH_FT / 2, 220, PATTERN.BREAK_ALT_FT - CARRIER.DECK_HEIGHT_FT, world)
        return { world, headingDeg: brc, altFt: PATTERN.BREAK_ALT_FT, ktas: PATTERN.INITIAL_SPEED_KTS }

      case 'DOWNWIND':
        c.shipToWorld(
          2000,
          -PATTERN.DOWNWIND_ABEAM_DISTANCE_NM * 6076.1155,
          PATTERN.DOWNWIND_ALT_FT - CARRIER.DECK_HEIGHT_FT,
          world,
        )
        return {
          world,
          headingDeg: wrapDeg360(brc + 180),
          altFt: PATTERN.DOWNWIND_ALT_FT,
          ktas: 250,
        }

      case 'APPROACH_TURN':
        c.shipToWorld(
          -370,
          -PATTERN.DOWNWIND_ABEAM_DISTANCE_NM * 6076.1155,
          PATTERN.ABEAM_ALT_FT - CARRIER.DECK_HEIGHT_FT,
          world,
        )
        return {
          world,
          headingDeg: wrapDeg360(brc + 180),
          altFt: PATTERN.ABEAM_ALT_FT,
          ktas: onSpeedKtas(RECOVERY_WEIGHT_LBS),
        }

      case 'GROOVE': {
        // On the extended angled-deck centreline, on the ball, crabbed so the
        // aeroplane tracks the line rather than the deck heading.
        const rangeFt = PATTERN.GROOVE_LENGTH_NM * 6076.1155
        const eyeAlt =
          CARRIER.DECK_HEIGHT_FT +
          AIRCRAFT.HOOK_TO_EYE_FT +
          (rangeFt + TARGET_TOUCHDOWN_FT) * Math.tan(CARRIER.GLIDESLOPE_DEG * DEG)
        const altFt = eyeAlt - AIRCRAFT.EYE_ABOVE_ORIGIN_FT
        c.deckToWorld(-rangeFt, 0, altFt - CARRIER.DECK_HEIGHT_FT, world)
        const ktas = onSpeedKtas(RECOVERY_WEIGHT_LBS)
        return { world, headingDeg: c.grooveHeadingDeg(ktas), altFt, ktas }
      }

      default: {
        // The initial: three miles astern on base recovery course, slightly
        // starboard of the wake so the break happens over the bow.
        c.shipToWorld(
          -PATTERN.INITIAL_RANGE_NM * 6076.1155,
          220,
          PATTERN.INITIAL_ALT_FT - CARRIER.DECK_HEIGHT_FT,
          world,
        )
        return {
          world,
          headingDeg: brc,
          altFt: PATTERN.INITIAL_ALT_FT,
          ktas: PATTERN.INITIAL_SPEED_KTS,
        }
      }
    }
  }

  /* =================================================================== *
   *  STEP                                                               *
   * =================================================================== */

  get leg() {
    return LEGS[this.legIndex]
  }

  get isComplete() {
    return this.leg.id === 'COMPLETE'
  }

  /**
   * Advance one fixed timestep. The caller is responsible for scaling dt — a
   * held pattern is simply a dt of zero, applied to the ship as well so the
   * geometry stays consistent.
   */
  step(dt) {
    if (dt <= 0) {
      // Frozen, but still thinking. The leg-change check has to keep running
      // or the hold never lifts: the pattern would stop for a missing gear
      // handle and then ignore you putting it down, because nothing was
      // advancing to notice. A held pattern is a paused world, not a dead one.
      this._syncState(0)
      this._updateNav(0)
      this._maybeAdvance()
      return
    }

    this.time += dt
    this.legTime += dt

    const leg = this.leg
    const onDeck = !!leg.onDeck

    // ---- speed ----------------------------------------------------------
    const targetKtas = this._targetSpeed(leg) + (onDeck ? 0 : this._gust(leg).ktas)
    const accel = onDeck ? 46 : this.ktas > targetKtas ? (leg.decelKtS ?? 4.2) : 3.0
    // Ease the deceleration in and out over its first and last few knots. A
    // constant rate that switches off the instant it arrives is a step in
    // longitudinal acceleration, and because alpha is solved from speed that
    // step is visible as a kink in the pitch attitude.
    const gap = Math.abs(targetKtas - this.ktas)
    const ease = clamp(gap / 6, 0.12, 1)
    const dv = clamp(targetKtas - this.ktas, -accel * ease * dt, accel * ease * dt)
    this.ktas = Math.max(0, this.ktas + dv)

    // ---- bank -----------------------------------------------------------
    // Two integrations, not one. The guidance asks for a bank angle; the pilot
    // answers with a ROLL RATE proportional to how far out it is; and that rate
    // is itself reached at a finite roll acceleration. Bank angle is then the
    // integral of the rate.
    //
    // Doing it in one step — clamping the change in bank angle directly — is
    // what made the aeroplane look mechanical. Roll rate went from nothing to
    // its limit between two frames and back to nothing just as abruptly, so
    // every turn in the pattern began and ended with a snap. Nothing else in
    // the trainer was wrong; it just moved like a mechanism.
    const air = this._gust(leg)
    const bankCmd = onDeck ? 0 : this._commandedBank(leg) + air.bankDeg
    const before = this.headingDeg

    const rateCmd = clamp(
      (bankCmd - this.bankDeg) * ROLL_RATE_PER_DEG,
      -ROLL_RATE_DEG_S,
      ROLL_RATE_DEG_S,
    )
    this.rollRateDegS += clamp(
      rateCmd - this.rollRateDegS,
      -ROLL_ACCEL_DEG_S2 * dt,
      ROLL_ACCEL_DEG_S2 * dt,
    )
    this.bankDeg += this.rollRateDegS * dt

    // ---- heading, from the bank the wing is actually at -----------------
    const vFps = Math.max(this.ktas * KT_TO_FPS, 1)
    if (!onDeck) {
      const turnRate = ((G_FPS2 * Math.tan(this.bankDeg * DEG)) / vFps) * RAD
      this.headingDeg = wrapDeg360(this.headingDeg + turnRate * dt)
    } else {
      this.headingDeg = this.carrier.deckHeadingDeg
    }
    this.turnedDeg += Math.abs(deltaDeg(this.headingDeg, before))

    // ---- vertical -------------------------------------------------------
    const targetAlt = this._targetAltitude(leg)
    if (onDeck) {
      // Settle onto the wheels rather than snapping to them.
      //
      // The glidepath is flown in reference-point altitude, and at the moment
      // the hook touches, the nose-up attitude has the reference point 10.6 ft
      // above the deck. Parked on the wheels it is 8.1 ft. Assigning the
      // parked figure directly the frame after touchdown dropped the whole
      // aeroplane nearly three metres in one 8 ms step — a visible thump
      // through the deck from every camera, and the jet buried to the intakes
      // for the rest of the rollout, because the mains hang 8 ft below the
      // origin.
      const floorFt = CARRIER.DECK_HEIGHT_FT + AIRCRAFT.GEAR_STATIC_HEIGHT_FT
      if (this.altFt > floorFt + 0.05) {
        this.vsFpm = damp(this.vsFpm ?? 0, -140, 3.0, dt)
        this._vsControl = this.vsFpm
        this.altFt = Math.max(floorFt, this.altFt + (this.vsFpm / 60) * dt)
      } else {
        // ON THE WHEELS. Both of these have to be zeroed, and the second one
        // is not cosmetic.
        //
        // Flight path angle is asin(sink / speed), and a stopped aeroplane has
        // no speed — the integrator floors it at 1 ft/s to keep the turn rate
        // finite. So leaving a residual 140 fpm of sink in the vertical
        // channel meant asin(-2.3 / 1), which clamps to asin(-1): the parked
        // Tomcat pitched ninety degrees nose down and stood on its radome in
        // the middle of the flight deck. Correct altitude, correct heading,
        // and vertical.
        this.altFt = floorFt
        // Bled off rather than zeroed. The wheels arrive doing about 320 fpm
        // and stopping that in a single frame is a 38,000 fpm/s step — by a
        // wide margin the largest discontinuity left anywhere in the pattern,
        // and visible as a flinch at the exact moment you are watching for the
        // touchdown. Oleo struts take about a fifth of a second.
        this.vsFpm = damp(this.vsFpm ?? 0, 0, 14, dt)
        this._vsControl = this.vsFpm
      }
    } else {
      const maxVsFpm = this._onGlidepath ? 900 : 1500
      // A proportional altitude hold droops on a moving target: to sustain 700
      // fpm of descent it has to sit permanently below the glidepath by
      // however many feet the gain needs. On a 3.5 degree slope that came out
      // as 16 ft high at the ramp — a quarter of a ball, from a controller
      // that looked perfectly reasonable. So the glidepath's own rate of
      // descent is fed forward and the proportional term only trims the
      // residual.
      const feedFwdFpm = this._onGlidepath
        ? -this._closureFps * Math.tan(CARRIER.GLIDESLOPE_DEG * DEG) * 60
        : 0
      // The trim authority is only throttled back ON the glidepath, where the
      // feed-forward is doing the work and a big proportional term would just
      // hunt. Off it, the clamp has to come off: with 320 fpm the downwind
      // needed thirty-seven seconds to lose the 200 ft from the break altitude
      // to pattern altitude, and the downwind is fifteen seconds long. The jet
      // arrived at the abeam a hundred and thirty feet high and every number
      // after it was wrong.
      const trim = this._onGlidepath
        ? clamp((targetAlt - this.altFt) * 36, -320, 320)
        : clamp((targetAlt - this.altFt) * 36, -maxVsFpm, maxVsFpm)
      const wantedFpm = clamp(feedFwdFpm + trim, -maxVsFpm, maxVsFpm)
      // Rate-limit the COMMAND before the aeroplane chases it. A leg boundary
      // swaps one altitude law for another between two frames, and without this
      // the jet inherits that discontinuity directly as a pitch snap.
      this._wantedFpm = (this._wantedFpm ?? wantedFpm) +
        clamp(wantedFpm - (this._wantedFpm ?? wantedFpm), -VS_CMD_RATE_FPM_S * dt, VS_CMD_RATE_FPM_S * dt)

      // THE CONTROLLER'S OWN STATE, kept separate from what the aeroplane is
      // actually doing. This distinction is not pedantry — it was a bug worth
      // sixty-five feet of unwanted climb on the initial.
      //
      // Adding the gust to `vsFpm` and then damping `vsFpm` toward the command
      // next frame feeds the gust back into the filter it is supposed to be
      // disturbing. At 120 Hz with a damping constant of 3.4 the filter only
      // sheds 2.8% of its state per step, so a 90 fpm gust re-added every frame
      // accumulates to about thirty-five times itself: three thousand feet a
      // minute of phantom climb from a disturbance meant to be barely visible.
      this._vsControl = damp(this._vsControl ?? 0, this._wantedFpm, 3.4, dt)

      // The air, added on TOP of the controller each frame and never folded
      // back into it. The controller still rejects it, but through the altitude
      // error it causes — which is exactly how a real one would.
      this.vsFpm =
        this._vsControl +
        air.vsFpm +
        burbleFpm(this.nav.toTouchdownFt, this._turbScale * ENV.TURBULENCE, this._onFinal)
      this.altFt += (this.vsFpm / 60) * dt
    }

    // ---- position -------------------------------------------------------
    const gamma = onDeck ? 0 : Math.atan2(this.vsFpm / 60, vFps)
    const h = this.headingDeg * DEG
    const speedFps = onDeck ? this.ktas * KT_TO_FPS : vFps
    this._v.set(
      Math.sin(h) * Math.cos(gamma) * speedFps,
      Math.sin(gamma) * speedFps,
      -Math.cos(h) * Math.cos(gamma) * speedFps,
    )
    this.position.x += this._v.x * dt * FT_TO_M
    this.position.z += this._v.z * dt * FT_TO_M

    if (onDeck) {
      // Once you are in the wires you are part of the ship, and the ship is
      // doing 30 knots. Without this the carrier steams out from under a
      // stationary aeroplane at fifty feet a second: by the time you have read
      // the debrief you are off the back of the angled deck and hovering over
      // open water, which is a memorable way to end a perfect pass.
      //
      // The floor on `vFps` above is also why this cannot simply reuse it —
      // a stopped aeroplane was still creeping forward at 1 ft/s.
      this.position.addScaledVector(this.carrier.velocity, dt)
    }

    this.position.y = this.altFt * FT_TO_M

    // ---- configuration --------------------------------------------------
    this.flapNorm = damp(this.flapNorm, leg.flapNorm ?? 0, 1.4, dt)

    // Wing sweep runs itself. The programmer commands from Mach; the wings get
    // there at the rate the screwjacks manage, which is what makes it visible
    // rather than instantaneous.
    const sweepCmd = autoSweepDeg((this.ktas * KT_TO_FPS) / 1116)
    this.sweepDeg += clamp(
      sweepCmd - this.sweepDeg,
      -SWEEP_RATE_DEG_S * dt,
      SWEEP_RATE_DEG_S * dt,
    )

    this._syncState(dt)
    this._updateNav(dt)
    this._maybeAdvance()
  }

  /* ------------------------------------------------------------------ */

  /**
   * The air, this frame. Cached per step so the bank, the speed and the
   * vertical channel are all being pushed by the SAME gust — three independent
   * evaluations of the same function at the same instant would be three
   * different gusts, and the aeroplane would twitch rather than ride.
   */
  _gust(leg) {
    if (this._gustAt === this.time) return this._gustCache
    // "On final" is the glidepath being latched, not a range — see air.js.
    this._onFinal = !!this._onGlidepath || !!leg.glideslope
    this._turbScale = leg.onDeck
      ? 0
      : turbulenceScale(this.nav.toTouchdownFt, !!this._onGlidepath, this._onFinal)
    this._gustCache = gust(this.time, this._turbScale, ENV.TURBULENCE)
    this._gustAt = this.time
    return this._gustCache
  }

  _targetSpeed(leg) {
    if (leg.speedKtas === 0) return 0
    if (leg.speedKtas) return leg.speedKtas
    // On speed means on ANGLE OF ATTACK, not on airspeed. In a 27 degree bank
    // the wing needs 12% more lift, so holding the doughnut means the speed
    // comes up — which is exactly what happens in the aeroplane and why the
    // approach turn is flown a few knots fast.
    const nz = 1 / Math.max(Math.cos(this.bankDeg * DEG), 0.35)
    return onSpeedKtas(this.weightLbs, this.altFt, nz)
  }

  /**
   * Flap position for the aerodynamics: the pilot's handle when there is a
   * pilot, the leg schedule when the pattern is being flown by a test.
   */
  get currentFlapNorm() {
    const fromPilot = this.flapSource?.()
    return Number.isFinite(fromPilot) ? fromPilot : this.flapNorm
  }

  /**
   * The glidepath, in aircraft-reference-point altitude.
   *
   * It runs through the TARGET TOUCHDOWN POINT at EYE height, not through the
   * ramp at deck level. Getting that wrong puts you half a ball low the whole
   * way down and looks entirely plausible while it does it.
   */
  _glidepathAltFt() {
    const toTouchdown = Math.max(this.nav.toTouchdownFt, 0)
    const eyeAlt =
      CARRIER.DECK_HEIGHT_FT +
      AIRCRAFT.HOOK_TO_EYE_FT +
      toTouchdown * Math.tan(CARRIER.GLIDESLOPE_DEG * DEG)
    return eyeAlt - AIRCRAFT.EYE_ABOVE_ORIGIN_FT
  }

  _targetAltitude(leg) {
    this._onGlidepath = false

    if (leg.glideslope) {
      this._onGlidepath = true
      return this._glidepathAltFt()
    }

    if (leg.altFromTurn) {
      const [from, to] = leg.altFromTurn
      // SCHEDULED ON HEADING STILL TO TURN.
      //
      // The chart states the descent as three checkpoints — 600 ft at the
      // abeam, 450 to 500 at the 90, 360 at three quarters of a mile — and the
      // first two are named by how much turn is LEFT, not by where you are. So
      // that is the variable to schedule on, and it pins the 90 exactly instead
      // of arriving near it by luck.
      //
      // Two things it must not be scheduled on, both tried:
      //
      //   Degrees turned. Monotone, but it fixes the profile to one turn rate.
      //   Fly the same 190 degrees more slowly and you cover more ground doing
      //   it, so the descent meets the glidepath further out — the intercept
      //   wandered from 4,400 ft to 6,300 ft across the sweep, which is the
      //   difference between three quarters of a mile and a mile and a bit.
      //
      //   Range to the touchdown point. NOT MONOTONIC, which is the trap. The
      //   aeroplane swings close aboard the ship early in the turn, opens back
      //   out to 6,300 ft near the 90, and only then comes in. Scheduling on it
      //   drove the jet down to 360 ft while it was still 7,300 ft off the
      //   centreline with 150 degrees left to turn, and then — having latched
      //   the glidepath — it CLIMBED a hundred feet back up the cone as the
      //   range reopened. Every checkpoint still passed. The profile was
      //   nonsense.
      const toGoDeg = Math.abs(this.nav.grooveHeadingErrorDeg)
      const startDeg = Math.max(this._turnStartHeadingErrDeg, 100)
      const NINETY = 90
      const midAlt = (from + to) / 2 + 5 // the book's 450-500 band, centred
      const scheduled =
        toGoDeg > NINETY
          ? from + (midAlt - from) * clamp((startDeg - toGoDeg) / (startDeg - NINETY), 0, 1)
          : midAlt + (to - midAlt) * clamp((NINETY - toGoDeg) / NINETY, 0, 1)

      if (!leg.captureGlidepath) return scheduled

      // INTERCEPT the glideslope rather than sit on it from the abeam.
      //
      // NAVAIR fig 8-3 gives three points down the turn: 600 ft at the abeam,
      // 450 to 500 at the 90, and the glideslope joined at approximately three
      // quarters of a mile and 360 ft. They are not three separate rules —
      // they are one steady descent that happens to meet the glidepath at 3/4
      // mile, which is what "intercept" means.
      //
      // So the schedule runs 600 down to 360 across the turn and the glidepath
      // is latched the moment the two meet. Joining it at a mile and a bit,
      // which is what this used to do, put the jet fifty feet BELOW the book's
      // altitude at the 90 and turned an interception into a long shallow
      // drift.
      // Latch only on the way IN. The range opens back out through the middle
      // of the turn, so a latch taken early has the aeroplane following a
      // glidepath that is rising away from it.
      const astern = this.nav.toTouchdownFt > 1200
      const closing = this.nav.closureFps > 0
      const pastTheNinety = toGoDeg < NINETY + 8
      const gp = this._glidepathAltFt()
      if (
        astern &&
        (this._glidepathLatched || (closing && pastTheNinety && scheduled <= gp + 15))
      ) {
        this._glidepathLatched = true
        this._onGlidepath = true
        // Never command a climb. Between the break and the deck the aeroplane
        // only ever goes down, and this is the one line that guarantees it
        // whatever the geometry does.
        this._lastCommandedAlt = Math.min(this._lastCommandedAlt ?? gp, gp)
        return this._lastCommandedAlt
      }
      this._lastCommandedAlt = Math.min(this._lastCommandedAlt ?? scheduled, scheduled)
      return this._lastCommandedAlt
    }

    return leg.altFt ?? this.altFt
  }

  _commandedBank(leg) {
    const g = leg.guidance ?? { mode: 'track', originFt: [0, 0], headingOffsetDeg: 0 }

    if (g.mode === 'turn') {
      const blendBelow = g.blendToCentrelineBelowDeg
      if (blendBelow == null || Math.abs(this.nav.grooveHeadingErrorDeg) > blendBelow) {
        return -Math.abs(g.bankDeg)
      }
      // Past the handover point: fly the lineup, not the bank.
      return this._centrelineBank(g.blendMaxBankDeg ?? 30)
    }

    if (g.mode === 'groove') return this._centrelineBank(22)

    const [ox, oy] = g.originFt ?? [0, 0]
    const lineHeadingDeg = wrapDeg360(this.carrier.headingDeg + (g.headingOffsetDeg ?? 0))
    // Ship-frame offset from the line, rotated into the line's own frame.
    const dy = this.nav.ship.y - oy
    const dx = this.nav.ship.x - ox
    const th = (g.headingOffsetDeg ?? 0) * DEG
    const crossTrackFt = dy * Math.cos(th) - dx * Math.sin(th)

    return this._captureBank(crossTrackFt, lineHeadingDeg, CAPTURE_LEAD_FT, 30)
  }

  /** Bank to capture the extended angled-deck centreline. */
  _centrelineBank(maxBank) {
    // Cross-track IS the lineup error: deck-frame Y, positive right of the
    // centreline. Aim closer in than a normal track capture, because the
    // groove is short and a lazy intercept arrives at the ramp still drifting.
    //
    // The lead distance is the single most consequential number in the whole
    // approach turn, because it decides WHERE the turn rolls out — and where it
    // rolls out is the groove length. Aim close and the turn cuts the corner,
    // arriving lined up but only a third of a mile astern; aim far and it rolls
    // out wide and long. It is worth stating per leg rather than as a magic
    // constant buried in a ternary.
    const lead =
      this.leg.guidance?.captureLeadFt ??
      (this.leg.guidance?.mode === 'turn' ? 6000 : 2600)
    return this._captureBank(
      this.nav.deck.y,
      this.carrier.grooveHeadingDeg(this.ktas),
      lead,
      maxBank,
    )
  }

  /**
   * Aim at a point on the line ahead of us: far away when we are close to the
   * line, sharper when we are not. Same law a flight director uses, and it
   * settles rather than chasing.
   */
  _captureBank(crossTrackFt, lineHeadingDeg, leadFt, maxBank) {
    const interceptDeg = clamp(Math.atan2(-crossTrackFt, leadFt) * RAD, -45, 45)
    const wantedHeading = lineHeadingDeg + interceptDeg
    const err = deltaDeg(wantedHeading, this.headingDeg)
    return clamp(err * BANK_PER_DEG, -maxBank, maxBank)
  }

  /* ------------------------------------------------------------------ */

  _syncState(dt) {
    const s = this.state
    const leg = this.leg

    s.position.copy(this.position)
    s.altFt = this.altFt
    s.aglFt = this.altFt - (leg.onDeck ? CARRIER.DECK_HEIGHT_FT : 0)
    s.ktas = this.ktas
    // Below 1,000 ft on a standard day these are within a knot of each other.
    s.kcas = this.ktas
    s.mach = (this.ktas * KT_TO_FPS) / 1116
    s.vsFpm = this.vsFpm ?? 0

    const vFps = Math.max(this.ktas * KT_TO_FPS, 0.001)
    // On the deck the flight path angle is zero by definition; computing it
    // from sink over airspeed at a standstill is a divide-by-nothing dressed
    // up as trigonometry.
    // On the deck the flight path angle is zero, but it has to ARRIVE at zero
    // rather than be assigned it: the jet crosses the ramp at about two degrees
    // nose down and the leg boundary is one frame wide, so snapping the term
    // was a 200 deg/s pitch rate in the instant of touchdown.
    const gammaRaw = leg.onDeck ? 0 : Math.asin(clamp((s.vsFpm ?? 0) / 60 / vFps, -1, 1)) * RAD
    s.gammaDeg = leg.onDeck ? damp(s.gammaDeg, 0, 2.6, Math.max(dt, 1e-4)) : gammaRaw
    const gammaRad = s.gammaDeg * DEG

    const nz = 1 / Math.max(Math.cos(this.bankDeg * DEG), 0.35)
    s.nz = leg.onDeck ? 1 : nz
    s.weightLbs = this.weightLbs
    s.fuelLbs = AIRCRAFT.RESET_FUEL_LBS.reduce((a, b) => a + b, 0)

    s.alphaDeg = leg.onDeck
      ? damp(s.alphaDeg, 0, 1.8, Math.max(dt, 1e-4))
      : solveAlphaDeg({
          ktas: this.ktas,
          altFt: this.altFt,
          weightLbs: this.weightLbs,
          nz,
          flapNorm: this.currentFlapNorm,
        })
    s.aoaUnits = aoaUnits(s.alphaDeg)

    s.headingDeg = this.headingDeg
    s.rollDeg = this.bankDeg
    s.pitchDeg = s.gammaDeg + s.alphaDeg
    // A degree or so of sideslip from the same air that is moving the wings.
    // The velocity vector is unaffected — this is the nose hunting slightly
    // around the flight path, which is what the airframe visibly does.
    s.betaDeg = leg.onDeck ? 0 : (this._gustCache?.yawDeg ?? 0)

    this._euler.set(s.pitchDeg * DEG, -s.headingDeg * DEG, -s.rollDeg * DEG, 'YXZ')
    s.quaternion.setFromEuler(this._euler)
    s.forward.set(0, 0, -1).applyQuaternion(s.quaternion)
    s.right.set(1, 0, 0).applyQuaternion(s.quaternion)
    s.up.set(0, 1, 0).applyQuaternion(s.quaternion)

    const h = this.headingDeg * DEG
    s.velocity.set(
      Math.sin(h) * Math.cos(gammaRad) * vFps * FT_TO_M,
      Math.sin(gammaRad) * vFps * FT_TO_M,
      -Math.cos(h) * Math.cos(gammaRad) * vFps * FT_TO_M,
    )
    s.groundspeedKts = this.ktas * Math.cos(gammaRad)

    // REAL rates, not zeroes. These were hard-wired to nothing, which meant
    // anything downstream that wanted to know how fast the aeroplane was moving
    // — a camera with any lag in it, a gauge, a needle — was told it was not.
    if (dt > 0) {
      s.rollRateDegS = this.rollRateDegS ?? 0
      s.pitchRateDegS = (s.pitchDeg - (this._prevPitchDeg ?? s.pitchDeg)) / dt
      s.yawRateDegS = deltaDeg(s.headingDeg, this._prevHeadingDeg ?? s.headingDeg) / dt
      this._prevPitchDeg = s.pitchDeg
      this._prevHeadingDeg = s.headingDeg
    }

    // Written from the same source the aerodynamics used, so the model, the
    // indexer and the lift equation cannot disagree about where the flaps are.
    s.flapNorm = this.currentFlapNorm
    s.flapDeg = s.flapNorm * 35
    s.sweepDeg = this.sweepDeg ?? AIRCRAFT.SWEEP_MIN_DEG
    s.sweepNorm = clamp(
      (s.sweepDeg - AIRCRAFT.SWEEP_MIN_DEG) /
        (AIRCRAFT.SWEEP_MAX_DEG - AIRCRAFT.SWEEP_MIN_DEG),
      0,
      1,
    )
    s.weightOnWheels = !!leg.onDeck

    // Control surface positions are cosmetic here — they drive the animated
    // model, nothing else. Deriving them from the commanded attitude keeps the
    // stabilators and rudders moving in a way that matches what you see.
    s.elevatorDeg = clamp(-(s.alphaDeg - 4) * 0.55, -12, 12)
    s.aileronDeg = clamp(this.bankDeg * 0.25, -18, 18)
    s.rudderDeg = 0
  }

  _updateNav(dt) {
    const n = this.nav
    const c = this.carrier

    c.worldToShip(this.position, n.ship)
    c.worldToDeck(this.position, n.deck)

    const prev = n.toTouchdownFt
    n.toTouchdownFt = TARGET_TOUCHDOWN_FT - n.deck.x
    // Closure on the touchdown point, measured against the moving deck. This
    // is what the glidepath feed-forward needs and it is also the honest
    // answer to "how fast are you actually catching the boat".
    if (dt > 0) {
      this._closureFps = damp(this._closureFps, (prev - n.toTouchdownFt) / dt, 6, dt)
    }
    n.closureFps = this._closureFps
    n.rangeToRampFt = -n.deck.x
    n.headingDeg = this.headingDeg
    n.groundspeedKts = this.state.groundspeedKts
    n.turnedDeg = this.turnedDeg
    n.grooveHeadingErrorDeg = deltaDeg(this.headingDeg, c.grooveHeadingDeg(this.ktas))
    n.altFt = this.altFt

    // Abeam means abeam the LSO platform. Ask the ship where that is rather
    // than hard-coding a station number that would drift if the deck moved.
    c.lsoWorld(this._scratch)
    c.worldToShip(this._scratch, n.lso)
    n.lsoShipX = n.lso.x

    if (this.state.groundspeedKts < 3) this.stoppedFor += dt
    else this.stoppedFor = 0
    n.stoppedFor = this.stoppedFor

    if (this.leg.id === 'GROOVE') {
      if (this.grooveEnteredAt == null) this.grooveEnteredAt = this.time
      this.grooveTimeS = this.time - this.grooveEnteredAt
    }
  }

  _maybeAdvance() {
    const leg = this.leg
    if (!leg.done(this.nav)) {
      this.holding = false
      this.holdReason = ''
      return
    }

    const verdict = this.canLeaveLeg(leg.id)
    if (verdict === true || verdict == null) {
      this.holding = false
      this.holdReason = ''
      if (this.legIndex < LEGS.length - 1) {
        this.legIndex++
        this.legTime = 0
        this.turnedDeg = 0
        // The descent through the turn is scheduled between the range the turn
        // STARTS at and the book's glideslope intercept range, so both ends have
        // to be pinned the moment the leg begins.
        this._turnStartHeadingErrDeg = Math.abs(this.nav.grooveHeadingErrorDeg)
        this._lastCommandedAlt = null
        if (this.leg.id === 'TOUCHDOWN') this._recordTouchdown()
      }
      return
    }

    // Something on the checklist is not done. Stop the world rather than press
    // on without it: a trainer that lands you with the gear up has taught you
    // that the gear does not matter.
    this.holding = true
    this.holdReason = typeof verdict === 'string' ? verdict : 'waiting'
  }

  _recordTouchdown() {
    const d = { ...this.nav.deck }
    this.touchdown = d
    // Which pendant the hook actually caught. On rails it is the 3 wire, but
    // computing it rather than asserting it means the number stays honest if
    // anyone retunes the glideslope.
    let best = 0
    let bestDist = Infinity
    CARRIER.WIRES_FT.forEach((x, i) => {
      const dist = Math.abs(x - d.x)
      if (dist < bestDist) {
        bestDist = dist
        best = i + 1
      }
    })
    this.wire = best
  }

  /** Ball, lineup and closure, for the HUD and the IFLOLS. */
  approach() {
    const eye = this._scratch
      .copy(this.position)
      .addScaledVector(this.state.up, AIRCRAFT.EYE_ABOVE_ORIGIN_FT * FT_TO_M)
    return this.carrier.approachGeometry(eye, this.state.velocity)
  }
}

/* ===================================================================== *
 *  STATE                                                                *
 * ===================================================================== */

/**
 * The aircraft state, shaped exactly like the old FDM-sampled one.
 *
 * That is deliberate. The scene, the camera rig, the HUD and the cockpit all
 * read this object, and none of them should have to know or care that there is
 * no longer a flight model underneath. Keeping the shape identical is what
 * made replacing the physics a contained change instead of a rewrite.
 */
function makeState() {
  return {
    position: new THREE.Vector3(),
    velocity: new THREE.Vector3(),
    quaternion: new THREE.Quaternion(),
    forward: new THREE.Vector3(0, 0, -1),
    right: new THREE.Vector3(1, 0, 0),
    up: new THREE.Vector3(0, 1, 0),

    altFt: 0,
    aglFt: 0,
    kcas: 0,
    ktas: 0,
    groundspeedKts: 0,
    mach: 0,
    vsFpm: 0,
    headingDeg: 0,
    pitchDeg: 0,
    rollDeg: 0,
    alphaDeg: 0,
    betaDeg: 0,
    aoaUnits: 0,
    nz: 1,
    weightLbs: RECOVERY_WEIGHT_LBS,
    fuelLbs: 0,
    thrustLbs: 0,
    n2: 0,
    rollRateDegS: 0,
    pitchRateDegS: 0,
    yawRateDegS: 0,

    gearNorm: 0,
    flapNorm: 0,
    flapDeg: 0,
    speedbrakeNorm: 0,
    dlcNorm: 0,
    hookNorm: 0,
    sweepNorm: 0,
    sweepDeg: AIRCRAFT.SWEEP_MIN_DEG,
    elevatorDeg: 0,
    aileronDeg: 0,
    rudderDeg: 0,
    throttleNorm: 0,
    augmenting: false,
    weightOnWheels: false,
    gammaDeg: 0,
  }
}

function makeNav() {
  return {
    ship: { x: 0, y: 0, z: 0 },
    deck: { x: 0, y: 0, z: 0 },
    lso: { x: 0, y: 0, z: 0 },
    lsoShipX: -370,
    toTouchdownFt: 0,
    rangeToRampFt: 0,
    headingDeg: 0,
    groundspeedKts: 0,
    turnedDeg: 0,
    grooveHeadingErrorDeg: 0,
    altFt: 0,
    stoppedFor: 0,
  }
}

export { LEG_BY_ID, M_TO_FT }
